import { useMutation, UseMutationOptions, useQuery, useQueryClient } from 'react-query';

import { deleteRequest, getRequest, postRequest, putRequest } from 'services/apiRequests';
import type { APIList, StringifiedUUID } from 'types';
import type { SPMScenario } from 'types/SPM';
import type { APIError } from 'utils/errors';

export type BaseSPMScenarioRequest = {
  name?: string;
  description?: string;
  is_system?: boolean;
  [key: string]: unknown;
};

export type SPMScenarioRequest = BaseSPMScenarioRequest & {
  source_scenario_uuid: StringifiedUUID;
};

export type CreateSPMScenarioResponse = { aggregation_id: string };

const getSPMScenarios = (planningCycleId: StringifiedUUID) =>
  getRequest<null, APIList<SPMScenario>>(`/api/v1/tq/planning-cycles/${planningCycleId}/scenarios/`);

const useGetSPMScenariosQuery = (planningCycleId: StringifiedUUID) => {
  const QUERY_KEY = ['spm-scenarios', planningCycleId];
  const queryClient = useQueryClient();

  const query = useQuery<APIList<SPMScenario>, APIError>({
    queryKey: QUERY_KEY,
    queryFn: () => getSPMScenarios(planningCycleId),
    enabled: !!planningCycleId,
  });

  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey: QUERY_KEY });

  return { query, invalidateQuery };
};

const createSPMScenario = (csrfToken: string, planningCycleId: StringifiedUUID) => (scenario: SPMScenarioRequest) => {
  return postRequest<SPMScenarioRequest, CreateSPMScenarioResponse>(
    `/api/v1/tq/planning-cycles/${planningCycleId}/scenarios/`,
    scenario,
    csrfToken,
  );
};

const useCreateSPMScenarioMutation = (
  csrfToken: string,
  planningCycleId: StringifiedUUID,
  options: UseMutationOptions<CreateSPMScenarioResponse, APIError, SPMScenarioRequest> = {},
) => {
  const queryClient = useQueryClient();

  return {
    mutation: useMutation<CreateSPMScenarioResponse, APIError, SPMScenarioRequest>({
      ...options,
      onSuccess: (data, variables, context) => {
        queryClient.invalidateQueries({ queryKey: ['planning-cycles', 'list'] });
        options.onSuccess?.(data, variables, context);
      },
      mutationFn: createSPMScenario(csrfToken, planningCycleId),
    }),
  };
};

const updateDefaultSPMScenario =
  (csrfToken: string, planningCycleId: StringifiedUUID) =>
  ({ is_system, scenarioId }: { scenarioId: StringifiedUUID; is_system: boolean }) => {
    return putRequest<BaseSPMScenarioRequest, SPMScenario>(
      `/api/v1/tq/planning-cycles/${planningCycleId}/scenarios/${scenarioId}/`,
      { is_system },
      csrfToken,
    );
  };

const useUpdateDefaultSPMScenarioMutation = (
  csrfToken: string,
  planningCycleId: StringifiedUUID,
  options: UseMutationOptions<SPMScenario, APIError, { scenarioId: StringifiedUUID; is_system: boolean }> = {},
) => {
  return {
    mutation: useMutation<SPMScenario, APIError, { scenarioId: StringifiedUUID; is_system: boolean }>({
      ...options,
      mutationFn: updateDefaultSPMScenario(csrfToken, planningCycleId),
    }),
  };
};

const updateSPMScenario =
  (csrfToken: string, planningCycleId: StringifiedUUID, scenarioId: StringifiedUUID) =>
  (values: BaseSPMScenarioRequest) => {
    return putRequest<BaseSPMScenarioRequest, SPMScenario>(
      `/api/v1/tq/planning-cycles/${planningCycleId}/scenarios/${scenarioId}/`,
      {
        name: values.name,
        description: values.description,
      },
      csrfToken,
    );
  };

const useUpdateSPMScenarioMutation = (
  csrfToken: string,
  planningCycleId: StringifiedUUID,
  scenarioId: StringifiedUUID,
  options: UseMutationOptions<SPMScenario, APIError, BaseSPMScenarioRequest> = {},
) => {
  return {
    mutation: useMutation<SPMScenario, APIError, BaseSPMScenarioRequest>({
      ...options,
      mutationFn: updateSPMScenario(csrfToken, planningCycleId, scenarioId),
    }),
  };
};

const deleteSPMScenario = (csrfToken: string, planningCycleId: StringifiedUUID) => (scenarioId: StringifiedUUID) => {
  return deleteRequest<null, void>(
    `/api/v1/tq/planning-cycles/${planningCycleId}/scenarios/${scenarioId}/`,
    null,
    csrfToken,
  );
};

const useDeleteSPMScenarioMutation = (
  csrfToken: string,
  planningCycleId: StringifiedUUID,
  options: UseMutationOptions<void, APIError, StringifiedUUID> = {},
) => {
  return {
    mutation: useMutation<void, APIError, StringifiedUUID>({
      ...options,
      mutationFn: deleteSPMScenario(csrfToken, planningCycleId),
    }),
  };
};

export const SPMScenariosService = {
  useGetSPMScenariosQuery,
  useCreateSPMScenarioMutation,
  useUpdateSPMScenarioMutation,
  useDeleteSPMScenarioMutation,
  useUpdateDefaultSPMScenarioMutation,
};
